import type { SegmentedControlProps } from "@mantine/core";
import { SegmentedControl } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { get } from "lodash-es";
import type { _TransformValues } from "octalog-base";
import React from "react";

import type { NestedKeyOf } from "../../../common/Utils";

interface SegmentedControlInputProps<
  Values extends object,
  TransformValues extends _TransformValues<Values>,
> extends Omit<SegmentedControlProps, "value" | "onChange"> {
  form: UseFormReturnType<Values, TransformValues>;
  formKey: NestedKeyOf<Values>;
  parseValue?: (value: string) => unknown;
  onChange?: SegmentedControlProps["onChange"];
}

export function SegmentedControlInput<Values extends object, TransformValues extends _TransformValues<Values>>({
  form,
  formKey,
  parseValue,
  onChange,
  ...props
}: SegmentedControlInputProps<Values, TransformValues>): React.JSX.Element {
  const { onChange: _formOnChange, value: _formValue, ...formInputProps } = form.getInputProps(formKey);
  void _formOnChange;

  const value = _formValue == null ? "" : `${get(form.values, formKey as keyof Values)}`;

  return (
    <SegmentedControl
      {...formInputProps}
      {...props}
      onChange={(nextValue) => {
        form.setFieldValue(formKey, (parseValue ? parseValue(nextValue) : nextValue) as never);
        onChange?.(nextValue);
      }}
      value={value}
    />
  );
}
